"use client";

import { InputNumber, Icon } from "@pamperedchef/design-system";
import * as styles from "./QuantitySelector.module.scss";

export interface QuantitySelectorProps {
	value: number;
	onChange: (value: number) => void;
	min?: number;
	max?: number;
	step?: number;
	disabled?: boolean;
	/**
	 * Validation message rendered below the control via DS InputNumber's
	 * error slot. Consumer-owned (e.g. stock cap, per-order limit).
	 */
	errorMessage?: string;
	ariaLabel?: string;
}

/**
 * [−] [ value ] [+] quantity pill. Pure UI; controlled by the caller.
 * `CircleIconButton` is used in place of `IconButton` until DS resolves a
 * `forwardRef` typing regression on the latter.
 */
export function QuantitySelector({
	value,
	onChange,
	min = 1,
	max = 99,
	step = 1,
	disabled = false,
	errorMessage,
	ariaLabel = "Quantity"
}: QuantitySelectorProps) {
	const hasError = !!errorMessage;
	const clamp = (n: number) => Math.max(min, Math.min(max, n));

	const decrement = () => {
		if (disabled) return;
		const next = clamp(value - step);
		if (next !== value) onChange(next);
	};

	const increment = () => {
		if (disabled) return;
		const next = clamp(value + step);
		if (next !== value) onChange(next);
	};

	const atMin = value <= min;
	const atMax = value >= max;

	return (
		<div
			className={styles["qty-wrap"]}
			data-component="QuantitySelector"
			data-disabled={disabled || undefined}
			data-invalid={hasError || undefined}
		>
			<div className={styles["qty"]}>
				<button
					type="button"
					aria-label="Decrease quantity"
					onClick={decrement}
					disabled={disabled || atMin}
					className={styles["qty__btn"]}
					data-disabled={disabled || atMin}
				>
					<Icon name="minus" />
				</button>
				<div className={styles["qty__value"]}>
					<InputNumber
						value={value}
						onChange={(v) => onChange(clamp(v))}
						minValue={min}
						maxValue={max}
						step={step}
						isDisabled={disabled}
						isInvalid={hasError}
						showControls={false}
						aria-label={ariaLabel}
					/>
				</div>
				<button
					type="button"
					aria-label="Increase quantity"
					onClick={increment}
					disabled={disabled || atMax}
					className={styles["qty__btn"]}
					data-disabled={disabled || atMax}
				>
					<Icon name="plus" />
				</button>
			</div>

			{hasError && (
				<div className={styles["qty__error"]} role="alert">
					<Icon name="error" width={16} height={16} />
					<p>{errorMessage}</p>
				</div>
			)}
		</div>
	);
}
